import { Alert, toast } from "@heroui/react"
import { Button } from "~/lib/heroui"
import { useState } from "react"
import { AppAlertIndicator } from "./AppAlertIndicator"
import { identifierFor, restoreAccount, storeAuthToken } from "../lib/auth"

type AccountRestorePromptProps = {
  login: string
  onCancel: () => void
  password: string
  purgeAt?: string
  redirectTo: string
}

/**
 * Reached when sign-in answers 403 `pending_deletion`: the password was right,
 * the account is just inside its deletion grace period. Restoring needs the
 * same login and password again, so they are carried over rather than asked
 * for twice.
 */
export function AccountRestorePrompt({
  login,
  onCancel,
  password,
  purgeAt,
  redirectTo,
}: AccountRestorePromptProps) {
  const [pending, setPending] = useState(false)

  // Never write "15 天" here: the deadline is the server's to decide, and a
  // hardcoded number goes wrong the moment the grace period changes.
  const deadline = purgeAt
    ? new Date(purgeAt).toLocaleDateString("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : undefined

  async function onRestore() {
    setPending(true)

    try {
      const { token } = await restoreAccount({
        identifier: identifierFor(login),
        password,
      })
      storeAuthToken(token)
      window.location.assign(redirectTo)
    } catch (err) {
      toast.danger(err instanceof Error ? err.message : "恢复失败，请稍后再试")
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <Alert status="warning">
        <AppAlertIndicator status="warning" />
        <Alert.Content>
          <Alert.Title>这个账号已提交注销</Alert.Title>
        </Alert.Content>
      </Alert>

      <p className="text-on-surface text-sm leading-relaxed">
        <span className="font-medium">{login}</span>{" "}
        {deadline ? `将于 ${deadline} 被彻底删除。` : "正在注销冷静期内。"}
        现在恢复可以保留全部成长记录，之后数据将无法找回。
      </p>

      <Button fullWidth isPending={pending} onPress={onRestore}>
        恢复账户
      </Button>

      <Button fullWidth onPress={onCancel} variant="secondary">
        暂不恢复
      </Button>
    </div>
  )
}
